import React, { useRef, useEffect, useState, useCallback } from 'react';
import undoIcon from '../../assets/undo.png';
import redoIcon from '../../assets/redo.png';
import boldIcon from '../../assets/bold.png';
import italicIcon from '../../assets/italic.png';
import underlineIcon from '../../assets/underline.png';
import strikeIcon from '../../assets/strikeout.png';
import quoteIcon from '../../assets/doublequotes.png';
import linkIcon from '../../assets/link.png';
import {
  List,
  ListOrdered,
  Image as ImageIcon,
  UploadCloud,
  Link2,
  X,
  Indent,
  Outdent,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  Palette,
} from 'lucide-react';
import { sanitizePastedHtml } from '../../utils/pasteSanitizer';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { compressImageFileOrDataUrl } from '../../utils/imageCompressor';
import ImageSelectionBox from './ImageSelectionBox';
import ImageWrapDropdown from './ImageWrapDropdown';
import ImageFloatingBar from './ImageFloatingBar';
import ImageOptionsSidebar from './ImageOptionsSidebar';
import { ImageOptionsRollerIcon } from './editorIcons';
import HeadingDropdown from './HeadingDropdown';
import FontDropdown from './FontDropdown';
import './RichTextEditor.css';

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder,
  error,
  showToolbar = true,
  className = '',
}) => {
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  const toolbarRef = useRef(null);
  const lastValueRef = useRef('');
  const savedRangeRef = useRef(null);
  const fileInputRef = useRef(null);
  const textColorInputRef = useRef(null);
  const bgColorInputRef = useRef(null);
  const draggedImgRef = useRef(null);
  const isDroppingImageRef = useRef(false);
  const dragImageCountRef = useRef(0);
  const dragIdRef = useRef('');
  const justDroppedRef = useRef(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  const [activeFormats, setActiveFormats] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageTab, setImageTab] = useState('upload'); // 'upload' or 'url'
  const [imageUrl, setImageUrl] = useState('');
  const [imageUrlAlt, setImageUrlAlt] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isProcessingImages, setIsProcessingImages] = useState(false);

  // Formatting state: headings, font, size, colors
  const [currentHeading, setCurrentHeading] = useState('p');
  const [currentFont, setCurrentFont] = useState('default');
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [currentTextColor, setCurrentTextColor] = useState('#0f172a');
  const [currentBgColor, setCurrentBgColor] = useState('transparent');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);

  // Selected Image state for resizing & alignment
  const [selectedImg, setSelectedImg] = useState(null);
  const [imageToolbarPos, setImageToolbarPos] = useState(null);
  const [currentWrapMode, setCurrentWrapMode] = useState('inline');
  const [showImageOptionsSidebar, setShowImageOptionsSidebar] = useState(false);
  const sidebarRef = useRef(null);

  // Sync external value to contenteditable DOM & ensure images are draggable
  useEffect(() => {
    if (contentRef.current && contentRef.current.innerHTML !== value) {
      if (value === '' && contentRef.current.innerHTML === '<br>') return;
      if (value === lastValueRef.current) return;
      contentRef.current.innerHTML = sanitizeHtml(value || '');
      lastValueRef.current = value || '';
    }
    if (contentRef.current) {
      const imgs = contentRef.current.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.hasAttribute('draggable')) {
          img.setAttribute('draggable', 'true');
        }
      });
    }
  }, [value]);

  // CRITICAL: Native beforeinput listener to block Chromium's insertFromDrop.
  // React's synthetic onBeforeInput does NOT fire for InputEvent.inputType values
  // like 'insertFromDrop' or 'deleteByDrag'. We must use a native listener.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const nativeBeforeInput = (e) => {
      if (e.inputType === 'insertFromDrop') {
        // If we are handling an internal image drag, block the browser's duplicate insertion
        if (draggedImgRef.current || isDroppingImageRef.current) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
      }
      // Also block deleteByDrag — we handle removal ourselves in handleDrop
      if (e.inputType === 'deleteByDrag') {
        if (draggedImgRef.current || isDroppingImageRef.current) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }
    };

    // Use capture phase to intercept before anything else
    el.addEventListener('beforeinput', nativeBeforeInput, true);
    return () => {
      el.removeEventListener('beforeinput', nativeBeforeInput, true);
    };
  }, []); // refs are stable, no deps needed

  const emitChange = useCallback(() => {
    if (contentRef.current && onChange) {
      const html = contentRef.current.innerHTML;
      const clean = (html === '<br>' || html === '<div><br></div>') ? '' : html;
      lastValueRef.current = clean;
      onChange(clean);
    }
  }, [onChange]);

  // Update floating toolbar position relative to editor content wrapper
  const updateToolbarPosition = useCallback((imgEl) => {
    if (!imgEl || !wrapperRef.current) return;
    const imgRect = imgEl.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();

    const barHeight = 40;
    const barWidth = 220;

    const imgTop = imgRect.top - wrapperRect.top;
    const imgBottom = imgRect.bottom - wrapperRect.top;
    const imgCenter = imgRect.left - wrapperRect.left + (imgRect.width / 2);

    // Clamp horizontal position so bar never overflows left or right edges
    const minCenter = barWidth / 2 + 12;
    const maxCenter = Math.max(minCenter, wrapperRect.width - barWidth / 2 - 12);
    const clampedLeft = Math.max(minCenter, Math.min(maxCenter, imgCenter));

    // Determine whether to place below or above image
    const spaceBelow = wrapperRect.height - imgBottom;
    const placeAbove = spaceBelow < (barHeight + 14) && imgTop > (barHeight + 14);

    const barTop = placeAbove
      ? Math.max(6, imgTop - barHeight - 8)
      : imgBottom + 8;

    setImageToolbarPos({
      top: barTop,
      left: clampedLeft,
      bottom: imgBottom,
      right: imgRect.right - wrapperRect.left,
      width: imgRect.width,
      height: imgRect.height,
      placeAbove,
    });
  }, []);

  // Text Wrapping handler matching Google Docs (Image 2 & 3)
  const handleSetWrapMode = useCallback((mode) => {
    if (!selectedImg) return;
    setCurrentWrapMode(mode);
    selectedImg.setAttribute('data-wrap-mode', mode);

    // Clean previous wrap classes
    selectedImg.classList.remove('rte-wrap-inline', 'rte-wrap-text', 'rte-break-text', 'rte-wrap-behind', 'rte-wrap-in-front');

    // Ensure wrapper span does not constrain display or floats
    const parent = selectedImg.parentElement;
    if (parent && parent.classList.contains('rte-image-wrapper')) {
      parent.style.display = 'contents';
    }

    const margin = selectedImg.getAttribute('data-margin') || '16px';

    switch (mode) {
      case 'inline':
        selectedImg.classList.add('rte-wrap-inline');
        selectedImg.style.display = 'inline-block';
        selectedImg.style.float = 'none';
        selectedImg.style.clear = 'none';
        selectedImg.style.verticalAlign = 'baseline';
        selectedImg.style.margin = '4px 8px';
        selectedImg.style.position = 'static';
        selectedImg.style.zIndex = 'auto';
        break;
      case 'wrap-text':
        selectedImg.classList.add('rte-wrap-text');
        selectedImg.style.display = 'block';
        selectedImg.style.float = selectedImg.style.float === 'right' ? 'right' : 'left';
        selectedImg.style.clear = 'none';
        selectedImg.style.margin = `8px ${margin} 8px 0`;
        selectedImg.style.position = 'static';
        selectedImg.style.zIndex = 'auto';
        break;
      case 'break-text':
        selectedImg.classList.add('rte-break-text');
        selectedImg.style.display = 'block';
        selectedImg.style.float = 'none';
        selectedImg.style.clear = 'both';
        selectedImg.style.margin = `${margin} auto`;
        selectedImg.style.position = 'static';
        selectedImg.style.zIndex = 'auto';
        break;
      case 'behind-text':
        selectedImg.classList.add('rte-wrap-behind');
        selectedImg.style.display = 'block';
        selectedImg.style.float = 'left';
        selectedImg.style.clear = 'none';
        selectedImg.style.marginRight = '-100%';
        selectedImg.style.marginBottom = '8px';
        selectedImg.style.position = 'relative';
        selectedImg.style.zIndex = '0';
        break;
      case 'in-front-text':
        selectedImg.classList.add('rte-wrap-in-front');
        selectedImg.style.display = 'block';
        selectedImg.style.float = 'left';
        selectedImg.style.clear = 'none';
        selectedImg.style.marginRight = '-100%';
        selectedImg.style.marginBottom = '8px';
        selectedImg.style.position = 'relative';
        selectedImg.style.zIndex = '5';
        break;
      default:
        break;
    }

    emitChange();
    setTimeout(() => updateToolbarPosition(selectedImg), 50);
  }, [selectedImg, emitChange, updateToolbarPosition]);

  const selectImage = useCallback((imgEl) => {
    if (selectedImg && selectedImg !== imgEl) {
      selectedImg.classList.remove('rte-selected-image');
    }
    imgEl.classList.add('rte-selected-image');
    imgEl.setAttribute('draggable', 'true');
    setSelectedImg(imgEl);

    // Detect wrap mode
    const savedMode = imgEl.getAttribute('data-wrap-mode');
    if (savedMode) {
      setCurrentWrapMode(savedMode);
    } else if (imgEl.classList.contains('rte-wrap-behind')) {
      setCurrentWrapMode('behind-text');
    } else if (imgEl.classList.contains('rte-wrap-in-front')) {
      setCurrentWrapMode('in-front-text');
    } else if (imgEl.classList.contains('rte-break-text') || imgEl.style.clear === 'both') {
      setCurrentWrapMode('break-text');
    } else if (imgEl.classList.contains('rte-wrap-text') || imgEl.style.float === 'left' || imgEl.style.float === 'right') {
      setCurrentWrapMode('wrap-text');
    } else {
      setCurrentWrapMode('inline');
    }

    updateToolbarPosition(imgEl);
  }, [selectedImg, updateToolbarPosition]);

  const deselectImage = useCallback(() => {
    if (selectedImg) {
      selectedImg.classList.remove('rte-selected-image');
      setSelectedImg(null);
      setImageToolbarPos(null);
      setShowImageOptionsSidebar(false);
    }
  }, [selectedImg]);

  // Click handler to select image, or place cursor anywhere around/near image or in content
  const handleContentClick = (e) => {
    if (justDroppedRef.current) {
      justDroppedRef.current = false;
      return;
    }

    // Direct click on an image
    if (e.target.tagName === 'IMG') {
      e.stopPropagation();
      selectImage(e.target);
      return;
    }

    // Always deselect any selected image when clicking elsewhere
    deselectImage();

    // If clicked on or inside an image wrapper (e.g. margin/padding around the image)
    const wrapper = e.target.closest('.rte-image-wrapper') || (e.target.classList && e.target.classList.contains('rte-image-wrapper') ? e.target : null);
    if (wrapper) {
      e.stopPropagation();
      const rect = wrapper.getBoundingClientRect();
      const range = document.createRange();
      if (e.clientX < rect.left + rect.width / 2) {
        range.setStartBefore(wrapper);
      } else {
        range.setStartAfter(wrapper);
      }
      range.collapse(true);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      contentRef.current?.focus();
      return;
    }

    // If user clicked in empty space of the editor container itself (e.g. below content)
    if (e.target === contentRef.current || (wrapperRef.current && e.target === wrapperRef.current)) {
      let range = null;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.collapse(true);
        }
      }

      if (range && contentRef.current && contentRef.current.contains(range.commonAncestorContainer)) {
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        contentRef.current.focus();
        return;
      }

      // If clicked below all existing paragraphs/images:
      if (contentRef.current) {
        contentRef.current.focus();
        let lastChild = contentRef.current.lastElementChild;
        if (!lastChild || (lastChild.tagName !== 'P' && lastChild.tagName !== 'DIV')) {
          const p = document.createElement('p');
          p.innerHTML = '<br>';
          contentRef.current.appendChild(p);
          lastChild = p;
        }
        const newRange = document.createRange();
        newRange.setStart(lastChild, 0);
        newRange.collapse(true);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
    }
  };

  // Double-click image to open Image Options sidebar directly (Google Docs behavior)
  const handleContentDoubleClick = (e) => {
    if (e.target.tagName === 'IMG') {
      e.stopPropagation();
      selectImage(e.target);
      setShowImageOptionsSidebar(true);
    }
  };

  // Delete selected image
  const handleDeleteSelectedImage = useCallback(() => {
    if (!selectedImg) return;
    const parent = selectedImg.parentElement;
    if (parent && parent.classList.contains('rte-image-wrapper') && parent.children.length === 1) {
      parent.remove();
    } else {
      selectedImg.remove();
    }
    deselectImage();
    emitChange();
  }, [selectedImg, deselectImage, emitChange]);

  // Keyboard shortcut to delete selected image (Delete or Backspace)
  useEffect(() => {
    if (!selectedImg) return;

    const handleKeyDown = (e) => {
      // Don't intercept if user is typing in an input or textarea
      const target = e.target;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          (target.isContentEditable && target !== contentRef.current))
      ) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        handleDeleteSelectedImage();
      } else if (e.key === 'Escape') {
        deselectImage();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        const imgToUse = selectedImg;
        deselectImage();

        const wrapper = imgToUse.closest('.rte-image-wrapper') || imgToUse;
        let block = wrapper;
        if (
          block.parentElement &&
          block.parentElement !== contentRef.current &&
          (block.parentElement.tagName === 'P' ||
            block.parentElement.tagName === 'DIV' ||
            block.parentElement.tagName === 'H1' ||
            block.parentElement.tagName === 'H2' ||
            block.parentElement.tagName === 'H3')
        ) {
          block = block.parentElement;
        }

        const newP = document.createElement('p');
        newP.innerHTML = '<br>';
        block.after(newP);

        const range = document.createRange();
        range.setStart(newP, 0);
        range.collapse(true);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        if (contentRef.current) {
          contentRef.current.focus();
        }
        emitChange();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();

        const imgToUse = selectedImg;
        deselectImage();

        const wrapper = imgToUse.closest('.rte-image-wrapper') || imgToUse;
        let block = wrapper;
        if (
          block.parentElement &&
          block.parentElement !== contentRef.current &&
          (block.parentElement.tagName === 'P' || block.parentElement.tagName === 'DIV')
        ) {
          block = block.parentElement;
        }

        let targetP = block.nextElementSibling;
        if (!targetP) {
          targetP = document.createElement('p');
          targetP.innerHTML = '<br>';
          block.after(targetP);
        }

        const range = document.createRange();
        range.setStart(targetP, 0);
        range.collapse(true);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        if (contentRef.current) {
          contentRef.current.focus();
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();

        const imgToUse = selectedImg;
        deselectImage();

        const wrapper = imgToUse.closest('.rte-image-wrapper') || imgToUse;
        let block = wrapper;
        if (
          block.parentElement &&
          block.parentElement !== contentRef.current &&
          (block.parentElement.tagName === 'P' || block.parentElement.tagName === 'DIV')
        ) {
          block = block.parentElement;
        }

        let prevBlock = block.previousElementSibling;
        if (!prevBlock) {
          prevBlock = document.createElement('p');
          prevBlock.innerHTML = '<br>';
          block.before(prevBlock);
        }

        const range = document.createRange();
        range.setStart(prevBlock, 0);
        range.collapse(true);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        if (contentRef.current) {
          contentRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedImg, handleDeleteSelectedImage, deselectImage, emitChange]);

  // Close toolbar when clicking outside editor or image
  useEffect(() => {
    const handleDocClick = (e) => {
      if (
        (toolbarRef.current && toolbarRef.current.contains(e.target)) ||
        (sidebarRef.current && sidebarRef.current.contains(e.target)) ||
        (selectedImg && selectedImg === e.target) ||
        (e.target.closest && (
          e.target.closest('.rte-gdocs-sidebar') ||
          e.target.closest('.rte-gdocs-floating-bar') ||
          e.target.closest('.rte-gdocs-selection-box') ||
          e.target.closest('.rte-gdocs-wrap-menu')
        ))
      ) {
        return;
      }
      deselectImage();
    };

    const handleScrollOrResize = () => {
      if (selectedImg) {
        updateToolbarPosition(selectedImg);
      }
    };

    document.addEventListener('mousedown', handleDocClick);
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    const currWrapper = wrapperRef.current;
    if (currWrapper) {
      currWrapper.addEventListener('scroll', handleScrollOrResize);
    }
    const currContent = contentRef.current;
    if (currContent) {
      currContent.addEventListener('scroll', handleScrollOrResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      if (currWrapper) {
        currWrapper.removeEventListener('scroll', handleScrollOrResize);
      }
      if (currContent) {
        currContent.removeEventListener('scroll', handleScrollOrResize);
      }
    };
  }, [selectedImg, deselectImage, updateToolbarPosition]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Ensure the selection is within our editor
      if (contentRef.current && contentRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
        return;
      }
    }
    savedRangeRef.current = null;
  };

  const restoreSelection = () => {
    if (contentRef.current) {
      contentRef.current.focus();
      if (savedRangeRef.current) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      }
    }
  };

  // Click-outside listener for color dropdowns
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.rte-color-picker-wrapper')) {
        setShowColorPicker(false);
        setShowBgColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const checkFormats = () => {
    const formats = [];
    if (document.queryCommandState('bold')) formats.push('bold');
    if (document.queryCommandState('italic')) formats.push('italic');
    if (document.queryCommandState('underline')) formats.push('underline');
    if (document.queryCommandState('strikeThrough')) formats.push('strikeThrough');
    if (document.queryCommandState('insertUnorderedList')) formats.push('insertUnorderedList');
    if (document.queryCommandState('insertOrderedList')) formats.push('insertOrderedList');
    if (document.queryCommandState('justifyLeft')) formats.push('justifyLeft');
    if (document.queryCommandState('justifyCenter')) formats.push('justifyCenter');
    if (document.queryCommandState('justifyRight')) formats.push('justifyRight');
    if (document.queryCommandState('justifyFull')) formats.push('justifyFull');

    const blockValue = (document.queryCommandValue('formatBlock') || '').toLowerCase();
    
    // Check if within a title, subtitle, or heading node
    const sel = window.getSelection();
    let detectedHeading = 'p';
    if (sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
      let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
      while (node && node !== contentRef.current) {
        if (node.classList && node.classList.contains('rte-heading-title')) {
          detectedHeading = 'title';
          break;
        }
        if (node.classList && node.classList.contains('rte-heading-subtitle')) {
          detectedHeading = 'subtitle';
          break;
        }
        const tag = node.tagName ? node.tagName.toLowerCase() : '';
        if (tag === 'h1') {
          detectedHeading = 'h1';
          break;
        }
        if (tag === 'h2') {
          detectedHeading = 'h2';
          break;
        }
        if (tag === 'h3') {
          detectedHeading = 'h3';
          break;
        }
        if (tag === 'h4' || tag === 'h5') {
          detectedHeading = 'subtitle';
          break;
        }
        node = node.parentElement;
      }
    } else {
      if (blockValue === 'blockquote') {
        formats.push('blockquote');
        detectedHeading = 'p';
      } else if (blockValue.includes('h1')) {
        detectedHeading = 'h1';
      } else if (blockValue.includes('h2')) {
        detectedHeading = 'h2';
      } else if (blockValue.includes('h3')) {
        detectedHeading = 'h3';
      } else if (blockValue.includes('h4') || blockValue.includes('h5')) {
        detectedHeading = 'subtitle';
      } else {
        detectedHeading = 'p';
      }
    }
    setCurrentHeading(detectedHeading);

    let detectedFont = '';
    const fontVal = document.queryCommandValue('fontName');
    if (fontVal) {
      detectedFont = fontVal.replace(/['"]/g, '').trim();
    }
    if (!detectedFont && sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
      let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
      if (node) {
        const computed = window.getComputedStyle(node);
        if (computed && computed.fontFamily) {
          detectedFont = computed.fontFamily.split(',')[0].replace(/['"]/g, '').trim();
        }
      }
    }
    if (detectedFont) {
      setCurrentFont(detectedFont);
    }

    setActiveFormats(formats);
  };

  const execCommand = (command, cmdValue = undefined) => {
    document.execCommand(command, false, cmdValue);
    if (contentRef.current) {
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
  };

  const handleHeadingChange = (styleId) => {
    restoreSelection();
    if (styleId === 'title') {
      try {
        document.execCommand('formatBlock', false, '<h1>');
      } catch {
        document.execCommand('formatBlock', false, 'h1');
      }
      const sel = window.getSelection();
      if (sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
        let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        while (node && node !== contentRef.current) {
          if (/^H[1-6]$/i.test(node.tagName)) {
            node.classList.add('rte-heading-title');
            node.classList.remove('rte-heading-subtitle');
            break;
          }
          node = node.parentElement;
        }
      }
      setCurrentHeading('title');
    } else if (styleId === 'subtitle') {
      try {
        document.execCommand('formatBlock', false, '<h4>');
      } catch {
        document.execCommand('formatBlock', false, 'h4');
      }
      const sel = window.getSelection();
      if (sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
        let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        while (node && node !== contentRef.current) {
          if (/^H[1-6]$/i.test(node.tagName) || node.tagName === 'P') {
            node.classList.add('rte-heading-subtitle');
            node.classList.remove('rte-heading-title');
            break;
          }
          node = node.parentElement;
        }
      }
      setCurrentHeading('subtitle');
    } else if (styleId === 'p') {
      try {
        document.execCommand('formatBlock', false, '<p>');
      } catch {
        document.execCommand('formatBlock', false, 'p');
      }
      const sel = window.getSelection();
      if (sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
        let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        while (node && node !== contentRef.current) {
          if (node.classList) {
            node.classList.remove('rte-heading-title');
            node.classList.remove('rte-heading-subtitle');
          }
          node = node.parentElement;
        }
      }
      setCurrentHeading('p');
    } else {
      // h1, h2, h3
      try {
        document.execCommand('formatBlock', false, `<${styleId}>`);
      } catch {
        document.execCommand('formatBlock', false, styleId);
      }
      const sel = window.getSelection();
      if (sel && sel.anchorNode && contentRef.current && contentRef.current.contains(sel.anchorNode)) {
        let node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
        while (node && node !== contentRef.current) {
          if (node.classList) {
            node.classList.remove('rte-heading-title');
            node.classList.remove('rte-heading-subtitle');
          }
          node = node.parentElement;
        }
      }
      setCurrentHeading(styleId);
    }

    if (contentRef.current) {
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
  };

  const handleFontFamilyChange = (fontFamily, weight = null, fontName = null) => {
    restoreSelection();
    if (!fontFamily || fontFamily === 'default') {
      document.execCommand('fontName', false, 'Arial');
      setCurrentFont('Arial');
    } else {
      const nameToApply = fontName || fontFamily.split(',')[0].replace(/['"]/g, '').trim();
      document.execCommand('fontName', false, nameToApply);
      setCurrentFont(nameToApply);

      if (weight && contentRef.current) {
        const fontEls = contentRef.current.querySelectorAll(`font[face*="${nameToApply}"]`);
        fontEls.forEach((el) => {
          el.style.fontWeight = weight;
        });
      }
    }
    if (contentRef.current) {
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
  };

  const handleFontSizeChange = (size) => {
    restoreSelection();
    if (!size) return;
    document.execCommand('fontSize', false, '7');
    if (contentRef.current) {
      const fontTags = contentRef.current.querySelectorAll('font[size="7"]');
      fontTags.forEach((el) => {
        el.removeAttribute('size');
        el.style.fontSize = size;
      });
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
    setCurrentFontSize(size);
  };

  const handleTextColor = (color) => {
    restoreSelection();
    document.execCommand('foreColor', false, color);
    setCurrentTextColor(color);
    setShowColorPicker(false);
    if (contentRef.current) {
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
  };

  const handleBgColor = (color) => {
    restoreSelection();

    if (color === 'transparent' || color === 'none') {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !contentRef.current) {
        setCurrentBgColor('transparent');
        setShowBgColorPicker(false);
        return;
      }

      const range = sel.getRangeAt(0);

      let targetRange = range;

      // If nothing is selected (cursor is collapsed):
      // Keep previous text's highlight 100% intact! Break caret out of the highlighted
      // container so that any further typing continues with NO highlight.
      if (targetRange.collapsed) {
        let node = targetRange.startContainer;
        let curr = node.nodeType === 1 ? node : node.parentElement;
        let highlightedAncestor = null;

        while (curr && curr !== contentRef.current) {
          const bg = curr.style?.backgroundColor || curr.style?.background;
          if ((bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') || curr.tagName === 'MARK') {
            highlightedAncestor = curr;
            break;
          }
          curr = curr.parentElement;
        }

        if (highlightedAncestor) {
          const zws = document.createTextNode('\u200B');
          const afterRange = document.createRange();
          afterRange.setStart(targetRange.startContainer, targetRange.startOffset);
          afterRange.setEnd(highlightedAncestor, highlightedAncestor.childNodes.length);
          const afterFrag = afterRange.extractContents();

          const grandParent = highlightedAncestor.parentNode;
          if (grandParent) {
            grandParent.insertBefore(zws, highlightedAncestor.nextSibling);

            if (afterFrag.textContent.length > 0 || (afterFrag.querySelector && afterFrag.querySelector('img'))) {
              const afterElem = highlightedAncestor.cloneNode(false);
              afterElem.appendChild(afterFrag);
              grandParent.insertBefore(afterElem, zws.nextSibling);
            }

            const newRange = document.createRange();
            newRange.setStart(zws, 1);
            newRange.setEnd(zws, 1);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }

        setCurrentBgColor('transparent');
        setShowBgColorPicker(false);
        if (contentRef.current) {
          contentRef.current.focus();
          emitChange();
        }
        return;
      }

      try {
        // 1. Extract the selected content
        const extracted = targetRange.extractContents();

        // 2. Clear background color from all elements in the extracted fragment
        const stripBg = (node) => {
          if (node.nodeType === 1) {
            if (node.style) {
              node.style.backgroundColor = '';
              node.style.background = '';
            }
            if (node.tagName === 'MARK') {
              const span = document.createElement('span');
              while (node.firstChild) span.appendChild(node.firstChild);
              node.replaceWith(span);
            }
          }
          if (node.childNodes) {
            Array.from(node.childNodes).forEach(stripBg);
          }
        };
        stripBg(extracted);

        // 3. Insert temporary marker at the exact extraction point
        const marker = document.createElement('span');
        targetRange.insertNode(marker);

        // 4. Split any ancestor with highlight background around the marker
        let curr = marker.parentElement;
        let safety = 0;
        while (curr && curr !== contentRef.current && safety < 30) {
          safety++;
          const bg = curr.style?.backgroundColor || curr.style?.background;
          const isMark = curr.tagName === 'MARK';
          if ((bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') || isMark) {
            const grandParent = curr.parentNode;
            if (!grandParent) break;

            const afterRange = document.createRange();
            afterRange.setStartAfter(marker);
            afterRange.setEnd(curr, curr.childNodes.length);
            const afterFrag = afterRange.extractContents();

            grandParent.insertBefore(marker, curr.nextSibling);

            if (afterFrag.textContent.length > 0 || (afterFrag.querySelector && afterFrag.querySelector('img'))) {
              const afterElem = curr.cloneNode(false);
              afterElem.appendChild(afterFrag);
              grandParent.insertBefore(afterElem, marker.nextSibling);
            }

            if (curr.textContent.length === 0 && (!curr.querySelector || !curr.querySelector('img'))) {
              curr.remove();
            }

            curr = marker.parentElement;
          } else {
            curr = curr.parentElement;
          }
        }

        // 5. Replace marker with the unhighlighted extracted content
        const parent = marker.parentNode;
        if (parent) {
          parent.replaceChild(extracted, marker);
        }

        // 6. Clean up empty spans
        if (contentRef.current) {
          contentRef.current.querySelectorAll('span').forEach((s) => {
            if (s.childNodes.length === 0 && !s.querySelector('img')) {
              s.remove();
            }
          });
        }
      } catch (err) {
        console.error('Error clearing highlight:', err);
      }

      setCurrentBgColor('transparent');
    } else {
      try {
        document.execCommand('hiliteColor', false, color);
      } catch {
        document.execCommand('backColor', false, color);
      }
      setCurrentBgColor(color);
    }

    setShowBgColorPicker(false);
    if (contentRef.current) {
      contentRef.current.focus();
      emitChange();
      checkFormats();
    }
  };

  const handleAlign = (alignType) => {
    restoreSelection();
    if (alignType === 'left') execCommand('justifyLeft');
    else if (alignType === 'center') execCommand('justifyCenter');
    else if (alignType === 'right') execCommand('justifyRight');
    else if (alignType === 'justify') execCommand('justifyFull');
  };

  const handleIndent = () => {
    restoreSelection();
    execCommand('indent');
  };

  const handleOutdent = () => {
    restoreSelection();
    execCommand('outdent');
  };

  const isActive = (command) => activeFormats.includes(command);

  // Insert arbitrary HTML fragment at current or saved caret position
  const insertHtmlAtCaret = (htmlToInsert) => {
    restoreSelection();

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && contentRef.current && contentRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();

      const tempEl = document.createElement('div');
      tempEl.innerHTML = htmlToInsert;
      const frag = document.createDocumentFragment();
      let node;
      let lastNode;
      while ((node = tempEl.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);

      if (lastNode) {
        if (lastNode.tagName === 'P') {
          range.setStart(lastNode, 0);
        } else {
          range.setStartAfter(lastNode);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else if (contentRef.current) {
      // Fallback: append to end of editor
      contentRef.current.focus();
      contentRef.current.innerHTML += htmlToInsert;
    }

    emitChange();
    checkFormats();
  };

  // Process and insert an array of image files (Base64 data URLs)
  const insertImageFiles = async (files) => {
    if (!files || files.length === 0) return;

    setIsProcessingImages(true);
    try {
      const readPromises = Array.from(files).map(async (file) => {
        try {
          const compressedSrc = await compressImageFileOrDataUrl(file);
          if (!compressedSrc) return null;
          return {
            src: compressedSrc,
            alt: file.name ? file.name.replace(/\.[^/.]+$/, '') : 'Blog Image',
          };
        } catch (err) {
          console.warn('Error compressing image file:', err);
          return null;
        }
      });

      const results = await Promise.all(readPromises);
      const validImages = results.filter(Boolean);
      if (validImages.length > 0) {
        let htmlToInsert = '';
        validImages.forEach((img) => {
          htmlToInsert += `<span class="rte-image-wrapper"><img src="${img.src}" alt="${img.alt}" class="rte-inline-image" draggable="true" /></span>&nbsp;`;
        });
        htmlToInsert += `<p><br></p>`;
        insertHtmlAtCaret(htmlToInsert);
        setTimeout(() => {
          if (contentRef.current) {
            const allImgs = contentRef.current.querySelectorAll('img');
            if (allImgs.length > 0) {
              selectImage(allImgs[allImgs.length - 1]);
            }
          }
        }, 80);
      }
    } finally {
      setIsProcessingImages(false);
      closeImageModal();
    }
  };

  const handlePaste = (e) => {
    // Check if clipboard contains files (e.g., screenshot or copied images)
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      const imgFiles = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
      if (imgFiles.length > 0) {
        e.preventDefault();
        insertImageFiles(imgFiles);
        return;
      }
    }

    const html = e.clipboardData.getData('text/html');
    if (html) {
      e.preventDefault();
      const clean = sanitizePastedHtml(html);
      if (clean) {
        document.execCommand('insertHTML', false, clean);
      } else {
        const text = e.clipboardData.getData('text/plain');
        if (text) document.execCommand('insertText', false, text);
      }
      emitChange();
      checkFormats();
      return;
    }

    const text = e.clipboardData.getData('text/plain');
    if (text) {
      e.preventDefault();
      document.execCommand('insertText', false, text);
      emitChange();
      checkFormats();
    }
  };

  // Drag and Drop handlers for images within the editor
  const handleDragStart = (e) => {
    const target = e.target;
    if (target && target.tagName === 'IMG') {
      const dragId = 'rte-img-' + Date.now();
      dragIdRef.current = dragId;
      target.setAttribute('data-rte-drag-id', dragId);
      draggedImgRef.current = target;
      isDroppingImageRef.current = false;
      setIsDraggingImage(true);
      target.classList.add('rte-is-dragging');

      if (contentRef.current) {
        dragImageCountRef.current = contentRef.current.querySelectorAll('img').length;
      }

      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        try {
          e.dataTransfer.setData('application/x-rte-image', dragId);
          e.dataTransfer.setData('text/plain', dragId);
        } catch (err) {}
      }
    }
  };

  const handleBeforeInput = (e) => {
    // Prevent Chromium from auto-inserting a duplicate copy of the image on drop
    if (e.inputType === 'insertFromDrop') {
      if (draggedImgRef.current || isDroppingImageRef.current || isDraggingImage) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.dataTransfer) {
        const types = Array.from(e.dataTransfer.types || []);
        if (types.includes('application/x-rte-image') || types.includes('Files')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }
  };

  const handleDragEnd = (e) => {
    if (e.target && e.target.tagName === 'IMG') {
      e.target.classList.remove('rte-is-dragging');
      e.target.removeAttribute('data-rte-drag-id');
    }
    if (draggedImgRef.current) {
      draggedImgRef.current.classList.remove('rte-is-dragging');
      draggedImgRef.current.removeAttribute('data-rte-drag-id');
    }
    setIsDraggingImage(false);
    setTimeout(() => {
      draggedImgRef.current = null;
      isDroppingImageRef.current = false;
      dragIdRef.current = '';
      dragImageCountRef.current = 0;
    }, 250);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }

    // Show live text insertion caret where image will land
    let range = null;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    } else if (e.rangeParent) {
      range = document.createRange();
      range.setStart(e.rangeParent, e.rangeOffset);
    }

    if (range && contentRef.current && contentRef.current.contains(range.commonAncestorContainer)) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handleDrop = (e) => {
    // 1. Internal image drag & drop FIRST.
    // IMPORTANT: Chrome populates e.dataTransfer.files with the <img> data even for
    // internal drags, so we must check draggedImgRef BEFORE checking files, otherwise
    // the file branch creates a duplicate copy instead of moving the existing node.
    if (draggedImgRef.current) {
      e.preventDefault();
      e.stopPropagation();
      isDroppingImageRef.current = true;
      justDroppedRef.current = true;

      const img = draggedImgRef.current;
      const dragId = dragIdRef.current;
      img.classList.remove('rte-is-dragging');

      // Determine the node to move (move wrapper if wrapped, or img directly)
      const oldParent = img.parentElement;
      const isWrapped = oldParent && oldParent.classList.contains('rte-image-wrapper');
      const nodeToMove = isWrapped ? oldParent : img;

      // Find drop caret range from mouse coordinates
      let range = null;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.collapse(true);
        }
      } else if (e.rangeParent) {
        range = document.createRange();
        range.setStart(e.rangeParent, e.rangeOffset);
      }

      if (range && contentRef.current && contentRef.current.contains(range.commonAncestorContainer)) {
        // Prevent dropping inside the node itself
        if (!nodeToMove.contains(range.startContainer)) {
          // Use temporary marker to prevent Range invalidation
          const marker = document.createElement('span');
          marker.className = 'rte-drop-marker';
          range.insertNode(marker);

          // Now safely detach nodeToMove from its old location
          nodeToMove.remove();
          if (!isWrapped && oldParent && oldParent.classList.contains('rte-image-wrapper') && oldParent.children.length === 0) {
            oldParent.remove();
          }

          // Replace marker with nodeToMove
          marker.replaceWith(nodeToMove);

          // Position caret right after the moved image
          const newRange = document.createRange();
          newRange.setStartAfter(nodeToMove);
          newRange.collapse(true);
          const sel = window.getSelection();
          if (sel) {
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }
      } else if (contentRef.current) {
        contentRef.current.appendChild(nodeToMove);
      }

      // Cleanup duplicate safeguard: Remove any duplicate copies that the browser might have created
      const cleanupDuplicates = () => {
        if (!contentRef.current) return;
        // 1. If any clone got tagged with the same dragId, remove all except the active img
        if (dragId) {
          const matchingImgs = contentRef.current.querySelectorAll(`img[data-rte-drag-id="${dragId}"]`);
          matchingImgs.forEach((dup) => {
            if (dup !== img) {
              const dupParent = dup.parentElement;
              if (dupParent && dupParent.classList.contains('rte-image-wrapper') && dupParent.children.length === 1) {
                dupParent.remove();
              } else {
                dup.remove();
              }
            }
          });
          img.removeAttribute('data-rte-drag-id');
        }

        // 2. Total image count safeguard: if count increased, find and remove the extra identical clone
        if (dragImageCountRef.current > 0) {
          const currentImgs = Array.from(contentRef.current.querySelectorAll('img'));
          if (currentImgs.length > dragImageCountRef.current) {
            // Find clones with matching src that are not our img
            const clones = currentImgs.filter((el) => el !== img && el.src === img.src);
            if (clones.length > 0) {
              const extra = clones[clones.length - 1];
              const p = extra.parentElement;
              if (p && p.classList.contains('rte-image-wrapper') && p.children.length === 1) {
                p.remove();
              } else {
                extra.remove();
              }
            }
          }
        }
      };

      // Run cleanup immediately and again after browser tick
      cleanupDuplicates();
      setTimeout(cleanupDuplicates, 50);

      selectImage(img);
      emitChange();

      // Reset drag state after browser event loop completes drop + beforeinput + input pipeline
      setTimeout(() => {
        setIsDraggingImage(false);
        draggedImgRef.current = null;
        isDroppingImageRef.current = false;
        dragIdRef.current = '';
        dragImageCountRef.current = 0;
      }, 250);

      return; // Done — don't fall through to external file handler
    }

    // 2. External image file drop from desktop (only reached if NOT an internal image drag)
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const imgFiles = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
      if (imgFiles.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        saveSelection();
        insertImageFiles(imgFiles);
        return;
      }
    }
  };

  const openImageModal = () => {
    saveSelection();
    setSelectedFiles([]);
    setPreviews([]);
    setImageUrl('');
    setImageUrlAlt('');
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedFiles([]);
    setPreviews([]);
    setImageUrl('');
    setImageUrlAlt('');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setSelectedFiles(files);
    const newPreviews = files.map((file) => ({
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' KB',
      url: URL.createObjectURL(file),
    }));
    setPreviews(newPreviews);
  };

  const handleInsertSelectedFiles = () => {
    if (selectedFiles.length > 0) {
      insertImageFiles(selectedFiles);
    }
  };

  const handleInsertUrlImage = () => {
    if (!imageUrl.trim()) return;
    let url = imageUrl.trim();
    if (!/^https?:\/\//i.test(url) && !url.startsWith('data:image/')) {
      url = 'https://' + url;
    }
    const alt = imageUrlAlt.trim() || 'Blog Image';
    const htmlToInsert = `<span class="rte-image-wrapper"><img src="${url}" alt="${alt}" class="rte-inline-image" draggable="true" /></span>&nbsp;<p><br></p>`;
    insertHtmlAtCaret(htmlToInsert);
    closeImageModal();
    setTimeout(() => {
      if (contentRef.current) {
        const allImgs = contentRef.current.querySelectorAll('img');
        if (allImgs.length > 0) {
          selectImage(allImgs[allImgs.length - 1]);
        }
      }
    }, 80);
  };

  // Handle Enter key inside editor to prevent caret trapping around image wrappers
  const handleEditorKeyDown = (e) => {
    if (e.key === 'Enter') {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const node = range.startContainer;

      let wrapper = null;
      if (node.nodeType === Node.ELEMENT_NODE) {
        wrapper = node.closest('.rte-image-wrapper') || (node.classList?.contains('rte-image-wrapper') ? node : null);
        if (!wrapper && node.childNodes) {
          const child = node.childNodes[range.startOffset];
          const prevChild = node.childNodes[range.startOffset - 1];
          if (child && child.classList?.contains('rte-image-wrapper')) {
            wrapper = child;
          } else if (prevChild && prevChild.classList?.contains('rte-image-wrapper')) {
            wrapper = prevChild;
          }
        }
      } else if (node.parentElement) {
        wrapper = node.parentElement.closest('.rte-image-wrapper');
      }

      if (wrapper) {
        e.preventDefault();
        e.stopPropagation();

        let block = wrapper;
        if (
          block.parentElement &&
          block.parentElement !== contentRef.current &&
          (block.parentElement.tagName === 'P' ||
            block.parentElement.tagName === 'DIV' ||
            block.parentElement.tagName === 'H1' ||
            block.parentElement.tagName === 'H2' ||
            block.parentElement.tagName === 'H3')
        ) {
          block = block.parentElement;
        }

        const newP = document.createElement('p');
        newP.innerHTML = '<br>';
        block.after(newP);

        deselectImage();

        const newRange = document.createRange();
        newRange.setStart(newP, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        contentRef.current.focus();
        emitChange();
      }
    }
  };

  return (
    <div className={`rich-text-editor ${error ? 'rich-text-editor-error' : ''} ${className || ''}`}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="rich-text-toolbar" role="toolbar">
          {/* Undo/Redo */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('undo')}
              className="toolbar-btn"
              title="Undo"
            >
              <img src={undoIcon} alt="Undo" draggable="false" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('redo')}
              className="toolbar-btn"
              title="Redo"
            >
              <img src={redoIcon} alt="Redo" draggable="false" />
            </button>
          </div>

          {/* Headings */}
          <div className="toolbar-group">
            <HeadingDropdown
              value={currentHeading}
              onChange={handleHeadingChange}
              onSaveSelection={saveSelection}
              onRestoreSelection={restoreSelection}
            />
          </div>

          {/* Font Family */}
          <div className="toolbar-group">
            <FontDropdown
              value={currentFont}
              onChange={handleFontFamilyChange}
              onSaveSelection={saveSelection}
              onRestoreSelection={restoreSelection}
            />
          </div>

          {/* Font Size */}
          <div className="toolbar-group">
            <select
              className="rte-select rte-size-select"
              value={currentFontSize}
              onMouseDown={saveSelection}
              onChange={(e) => handleFontSizeChange(e.target.value)}
              title="Font Size"
            >
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="24px">24px</option>
              <option value="28px">28px</option>
              <option value="36px">36px</option>
            </select>
          </div>

          {/* Formatting */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('bold')}
              className={`toolbar-btn ${isActive('bold') ? 'active' : ''}`}
              title="Bold"
            >
              <img src={boldIcon} alt="Bold" draggable="false" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('italic')}
              className={`toolbar-btn ${isActive('italic') ? 'active' : ''}`}
              title="Italic"
            >
              <img src={italicIcon} alt="Italic" draggable="false" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('underline')}
              className={`toolbar-btn ${isActive('underline') ? 'active' : ''}`}
              title="Underline"
            >
              <img src={underlineIcon} alt="Underline" draggable="false" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('strikeThrough')}
              className={`toolbar-btn ${isActive('strikeThrough') ? 'active' : ''}`}
              title="Strikethrough"
            >
              <img src={strikeIcon} alt="Strikethrough" draggable="false" />
            </button>
          </div>

          {/* Color & Highlight */}
          <div className="toolbar-group rte-color-group">
            {/* Text Color */}
            <div className="rte-color-picker-wrapper">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                }}
                onClick={() => {
                  setShowColorPicker(!showColorPicker);
                  setShowBgColorPicker(false);
                }}
                className={`toolbar-btn rte-color-btn ${showColorPicker ? 'active' : ''}`}
                title="Text Color"
              >
                <Palette className="w-4 h-4" />
                <span className="rte-color-bar" style={{ backgroundColor: currentTextColor }} />
              </button>
              {showColorPicker && (
                <div className="rte-color-dropdown">
                  <div className="rte-color-dropdown-title">Text Color</div>
                  <div className="rte-color-swatches">
                    {[
                      '#000000', '#334155', '#475569', '#64748B',
                      '#1E3A8A', '#2563EB', '#0284C7', '#0D9488',
                      '#16A34A', '#CA8A04', '#EA580C', '#DC2626',
                      '#9333EA', '#DB2777'
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        className="rte-color-swatch"
                        style={{ backgroundColor: color }}
                        onClick={() => handleTextColor(color)}
                        title={color}
                      />
                    ))}
                  </div>
                  <div className="rte-custom-color-row">
                    <span>Custom:</span>
                    <input
                      ref={textColorInputRef}
                      type="color"
                      value={currentTextColor.startsWith('#') ? currentTextColor : '#000000'}
                      onChange={(e) => handleTextColor(e.target.value)}
                      className="rte-custom-color-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Highlight Color */}
            <div className="rte-color-picker-wrapper">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                }}
                onClick={() => {
                  setShowBgColorPicker(!showBgColorPicker);
                  setShowColorPicker(false);
                }}
                className={`toolbar-btn rte-color-btn ${showBgColorPicker ? 'active' : ''}`}
                title="Highlight Color"
              >
                <Highlighter className="w-4 h-4" />
                <span className="rte-color-bar" style={{ backgroundColor: currentBgColor === 'transparent' ? '#cbd5e1' : currentBgColor }} />
              </button>
              {showBgColorPicker && (
                <div className="rte-color-dropdown">
                  <div className="rte-color-dropdown-title">Highlight Color</div>
                  <div className="rte-color-swatches">
                    {[
                      'transparent', '#FEF08A', '#FDE047', '#BBF7D0',
                      '#86EFAC', '#BFDBFE', '#93C5FD', '#FBCFE8',
                      '#F472B6', '#FED7AA', '#E9D5FF'
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        className={`rte-color-swatch ${color === 'transparent' ? 'rte-swatch-transparent' : ''}`}
                        style={{ backgroundColor: color === 'transparent' ? '#ffffff' : color }}
                        onClick={() => handleBgColor(color)}
                        title={color === 'transparent' ? 'None' : color}
                      >
                        {color === 'transparent' ? '✕' : ''}
                      </button>
                    ))}
                  </div>
                  <div className="rte-custom-color-row">
                    <span>Custom:</span>
                    <input
                      ref={bgColorInputRef}
                      type="color"
                      value={currentBgColor.startsWith('#') ? currentBgColor : '#FEF08A'}
                      onChange={(e) => handleBgColor(e.target.value)}
                      className="rte-custom-color-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Alignment */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAlign('left')}
              className={`toolbar-btn ${isActive('justifyLeft') ? 'active' : ''}`}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAlign('center')}
              className={`toolbar-btn ${isActive('justifyCenter') ? 'active' : ''}`}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAlign('right')}
              className={`toolbar-btn ${isActive('justifyRight') ? 'active' : ''}`}
              title="Align Right"
            >
              <AlignRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAlign('justify')}
              className={`toolbar-btn ${isActive('justifyFull') ? 'active' : ''}`}
              title="Justify"
            >
              <AlignJustify className="w-4 h-4" />
            </button>
          </div>

          {/* Indentation */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleOutdent}
              className="toolbar-btn"
              title="Decrease Indent"
            >
              <Outdent className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleIndent}
              className="toolbar-btn"
              title="Increase Indent"
            >
              <Indent className="w-4 h-4" />
            </button>
          </div>

          {/* Lists */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('insertUnorderedList')}
              className={`toolbar-btn ${isActive('insertUnorderedList') ? 'active' : ''}`}
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => execCommand('insertOrderedList')}
              className={`toolbar-btn ${isActive('insertOrderedList') ? 'active' : ''}`}
              title="Numbered List"
            >
              <ListOrdered className="w-4 h-4" />
            </button>
          </div>

          {/* Quote & Link */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (isActive('blockquote')) {
                  execCommand('formatBlock', 'div');
                } else {
                  execCommand('formatBlock', 'blockquote');
                }
              }}
              className={`toolbar-btn ${isActive('blockquote') ? 'active' : ''}`}
              title="Quote"
            >
              <img src={quoteIcon} alt="Quote" draggable="false" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                let url = prompt('Enter URL:');
                if (url) {
                  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
                    url = 'https://' + url;
                  }

                  const selection = window.getSelection();
                  const isCollapsed = !selection || selection.rangeCount === 0 || selection.getRangeAt(0).collapsed;

                  if (isCollapsed) {
                    execCommand('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
                  } else {
                    execCommand('createLink', url);
                  }
                }
              }}
              className="toolbar-btn"
              title="Link"
            >
              <img src={linkIcon} alt="Link" draggable="false" />
            </button>
          </div>

          {/* Multiple Images Insertion */}
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                saveSelection();
              }}
              onClick={openImageModal}
              className="toolbar-btn rte-image-btn"
              title="Insert Image(s) anywhere in blog (Upload multiple or via URL)"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Google Docs Image Controls in Top Toolbar when image is selected (Images 2, 3, 4) */}
          {selectedImg && (
            <div className="toolbar-group">
              <ImageWrapDropdown
                currentWrapMode={currentWrapMode}
                onSelectWrapMode={handleSetWrapMode}
                buttonClassName="toolbar-btn"
              />
              <button
                type="button"
                className={`toolbar-btn ${showImageOptionsSidebar ? 'active' : ''}`}
                onClick={() => setShowImageOptionsSidebar((prev) => !prev)}
                title="Image options"
              >
                <ImageOptionsRollerIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Editor Content Area */}
      <div
        className="rich-text-content-wrapper"
        ref={wrapperRef}
        onClick={handleContentClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div
          ref={contentRef}
          contentEditable
          onClick={handleContentClick}
          onDoubleClick={handleContentDoubleClick}
          onInput={emitChange}
          onBeforeInput={handleBeforeInput}
          onPaste={handlePaste}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onKeyDown={handleEditorKeyDown}
          onKeyUp={checkFormats}
          onMouseUp={(e) => {
            checkFormats();
            handleContentClick(e);
          }}
          onFocus={checkFormats}
          className="rich-text-content"
          data-placeholder={placeholder}
          spellCheck="true"
        />

        {/* Google Docs 8-handle Selection Box for resizing (Image 1) */}
        {selectedImg && !isDraggingImage && (
          <div className="rte-selection-box-container">
            <ImageSelectionBox
              selectedImg={selectedImg}
              wrapperRef={wrapperRef}
              contentRef={contentRef}
              onResize={() => updateToolbarPosition(selectedImg)}
              onResizeEnd={emitChange}
            />
          </div>
        )}

        {/* Google Docs Floating Bar below selected image (Images 2, 3, 4) */}
        {selectedImg && !isDraggingImage && (
          <ImageFloatingBar
            selectedImg={selectedImg}
            wrapperRef={wrapperRef}
            imageToolbarPos={imageToolbarPos}
            currentWrapMode={currentWrapMode}
            onSelectWrapMode={handleSetWrapMode}
            onToggleSidebar={() => setShowImageOptionsSidebar((prev) => !prev)}
            isSidebarOpen={showImageOptionsSidebar}
            onDeleteImage={handleDeleteSelectedImage}
          />
        )}

        {/* Google Docs Image Options Sidebar Drawer (Image 5) */}
        <ImageOptionsSidebar
          isOpen={showImageOptionsSidebar && !!selectedImg}
          onClose={() => setShowImageOptionsSidebar(false)}
          selectedImg={selectedImg}
          currentWrapMode={currentWrapMode}
          onSelectWrapMode={handleSetWrapMode}
          onChange={emitChange}
        />
      </div>

      {/* Insert Image Modal */}
      {showImageModal && (
        <div className="rte-modal-overlay" onClick={closeImageModal}>
          <div className="rte-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rte-modal-header">
              <div className="rte-modal-title">
                <ImageIcon className="w-5 h-5 text-primary" />
                <span>Insert Image(s) into Blog</span>
              </div>
              <button type="button" className="rte-close-btn" onClick={closeImageModal}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="rte-modal-tabs">
              <button
                type="button"
                className={`rte-tab-btn ${imageTab === 'upload' ? 'active' : ''}`}
                onClick={() => setImageTab('upload')}
              >
                <UploadCloud className="w-4 h-4" />
                Upload Images (Single or Multiple)
              </button>
              <button
                type="button"
                className={`rte-tab-btn ${imageTab === 'url' ? 'active' : ''}`}
                onClick={() => setImageTab('url')}
              >
                <Link2 className="w-4 h-4" />
                Image from URL
              </button>
            </div>

            <div className="rte-modal-body">
              {imageTab === 'upload' ? (
                <div className="rte-upload-panel">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />

                  <div
                    className="rte-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadCloud className="w-10 h-10 rte-dropzone-icon" />
                    <p className="rte-dropzone-primary">
                      Click to choose or drag & drop images
                    </p>
                    <p className="rte-dropzone-sub">
                      PNG, JPG, WEBP, GIF (You can select multiple images at once)
                    </p>
                  </div>

                  {previews.length > 0 && (
                    <div className="rte-preview-list">
                      <div className="rte-preview-header">
                        Selected {previews.length} image{previews.length > 1 ? 's' : ''}:
                      </div>
                      <div className="rte-preview-grid">
                        {previews.map((prev, idx) => (
                          <div key={idx} className="rte-preview-item">
                            <img src={prev.url} alt={prev.name} />
                            <span className="rte-preview-name">{prev.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="rte-modal-actions">
                    <button
                      type="button"
                      className="rte-cancel-btn"
                      onClick={closeImageModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rte-insert-btn"
                      onClick={handleInsertSelectedFiles}
                      disabled={selectedFiles.length === 0 || isProcessingImages}
                    >
                      {isProcessingImages ? 'Processing...' : `Insert ${selectedFiles.length > 0 ? selectedFiles.length : ''} Image${selectedFiles.length > 1 ? 's' : ''} at Cursor`}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rte-url-panel">
                  <div className="rte-form-group">
                    <label>Image Web Address (URL) *</label>
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      autoFocus
                    />
                  </div>
                  <div className="rte-form-group">
                    <label>Image Description / Alt Text (Optional)</label>
                    <input
                      type="text"
                      value={imageUrlAlt}
                      onChange={(e) => setImageUrlAlt(e.target.value)}
                      placeholder="e.g., Team working together"
                    />
                  </div>

                  {imageUrl.trim() && (
                    <div className="rte-url-preview">
                      <p>Preview:</p>
                      <img
                        src={imageUrl}
                        alt="Preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="rte-modal-actions">
                    <button
                      type="button"
                      className="rte-cancel-btn"
                      onClick={closeImageModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rte-insert-btn"
                      onClick={handleInsertUrlImage}
                      disabled={!imageUrl.trim()}
                    >
                      Insert Image at Cursor
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
